import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { BaseCurrencyReading, BaseCurrencyResponse } from '@/api/settings'
import { apiFetch } from '~/api/client'

export type { BaseCurrencyReading, BaseCurrencyResponse }

const BASE_CURRENCY_KEY = ['settings', 'base-currency'] as const

export function useBaseCurrency(): UseQueryResult<BaseCurrencyReading> {
  return useQuery({
    queryKey: BASE_CURRENCY_KEY,
    queryFn: () => apiFetch<BaseCurrencyReading>('/settings/base-currency'),
  })
}

/**
 * The save waits for the exchange rates as well as the row, because the two
 * together are what makes the switch true. A mutation that resolved on the
 * write alone would hand back control at the one moment every figure on every
 * page is wrong, and the reader would go looking at a total rather than at a
 * message about one.
 */
export function useSaveBaseCurrency(): UseMutationResult<BaseCurrencyResponse, Error, string> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (value: string) =>
      apiFetch<BaseCurrencyResponse>('/settings/base-currency', {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),
    // Every figure in the product is denominated by this, not just this card's.
    onSuccess: () => client.invalidateQueries(),
  })
}

/**
 * Every ISO 4217 code, from the runtime rather than a list kept here — the same
 * table the route validates against, so the menu cannot offer something the
 * save would refuse.
 */
export function currencyChoices(): Array<{ code: string; label: string }> {
  const names = displayNames()
  return Intl.supportedValuesOf('currency').map((code) => ({
    code,
    label: names === undefined ? code : `${code} — ${names.of(code) ?? code}`,
  }))
}

function displayNames(): Intl.DisplayNames | undefined {
  try {
    return new Intl.DisplayNames(undefined, { type: 'currency' })
  } catch {
    return undefined
  }
}
